import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import { Paper, Typography, Box, Button, Stack } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin
);

export default function FlexionChart({ 
  title, 
  timeSeriesData, 
  leftDataKey, 
  rightDataKey, 
  height = "400px",
  yAxisLabel = "Flexion (degrees)",
  leftColor = "#2196F3",
  rightColor = "#FF9800"
}) {
  const chartRef = React.useRef(null);
  if (!timeSeriesData || timeSeriesData.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 3, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          No {title} data available
        </Typography>
      </Paper>
    );
  }

  // Debug logging
  console.log("🎯 FLEXION CHART DEBUG - title:", title);
  console.log("🎯 FLEXION CHART DEBUG - leftDataKey:", leftDataKey);
  console.log("🎯 FLEXION CHART DEBUG - rightDataKey:", rightDataKey);
  console.log("🎯 FLEXION CHART DEBUG - timeSeriesData sample:", timeSeriesData[0]);
  console.log("🎯 FLEXION CHART DEBUG - left data sample:", timeSeriesData[0]?.[leftDataKey]);
  console.log("🎯 FLEXION CHART DEBUG - right data sample:", timeSeriesData[0]?.[rightDataKey]);

  // Prepare chart data
  const chartData = {
    labels: timeSeriesData.map((point, index) => point.time || index),
    datasets: [
      {
        label: `Left ${title.split(' ')[0]}`,
        data: timeSeriesData.map((point, index) => ({
          x: point.time || index,
          y: point[leftDataKey]
        })),
        borderColor: leftColor,
        backgroundColor: leftColor + '20',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      },
      {
        label: `Right ${title.split(' ')[0]}`,
        data: timeSeriesData.map((point, index) => ({
          x: point.time || index,
          y: point[rightDataKey]
        })),
        borderColor: rightColor,
        backgroundColor: rightColor + '20',
        borderWidth: 2,
        fill: false,
        tension: 0.1,
        pointRadius: 3,
        pointHoverRadius: 5,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      title: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: 'rgba(255, 255, 255, 0.3)',
        borderWidth: 1,
        callbacks: {
          title: function(tooltipItems) {
            const dataPoint = timeSeriesData[tooltipItems[0].dataIndex];
            return `Time: ${(dataPoint.time || tooltipItems[0].dataIndex).toFixed(2)}s`;
          },
          label: function(context) {
            const value = context.parsed.y.toFixed(2);
            const side = context.dataset.label.includes('Left') ? 'Left' : 'Right';
            return `${side}: ${value}°`;
          },
          afterBody: function(tooltipItems) {
            const dataIndex = tooltipItems[0].dataIndex;
            const leftValue = timeSeriesData[dataIndex]?.[leftDataKey];
            const rightValue = timeSeriesData[dataIndex]?.[rightDataKey];
            
            if (leftValue !== undefined && rightValue !== undefined) {
              const diff = Math.abs(leftValue - rightValue).toFixed(2);
              return `Difference: ${diff}° | Data Point: ${dataIndex + 1}`;
            }
            return `Data Point: ${dataIndex + 1}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        display: true,
        title: {
          display: true,
          text: 'Time (seconds)',
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: yAxisLabel,
          font: {
            size: 12,
            weight: 'bold'
          }
        },
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.1)'
        },
        beginAtZero: false
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'crosshair';
    },
    elements: {
      point: {
        hoverRadius: 8
      }
    },
    plugins: {
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true
          },
          mode: 'xy',
          scaleMode: 'xy'
        },
        pan: {
          enabled: true,
          mode: 'xy',
          scaleMode: 'xy'
        },
        limits: {
          y: {min: 'original', max: 'original'},
          x: {min: 'original', max: 'original'}
        }
      }
    }
  };

  // Chart control functions
  const resetZoom = () => {
    if (chartRef.current) {
      chartRef.current.resetZoom();
    }
  };

  const zoomIn = () => {
    if (chartRef.current) {
      chartRef.current.zoom(1.1);
    }
  };

  const zoomOut = () => {
    if (chartRef.current) {
      chartRef.current.zoom(0.9);
    }
  };

  const toggleDatasetVisibility = (datasetIndex) => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.hidden = !meta.hidden;
      chart.update();
    }
  };


  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      {/* Chart Controls */}
      <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {title}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => toggleDatasetVisibility(0)}
            sx={{ 
              color: leftColor, 
              borderColor: leftColor,
              '&:hover': { borderColor: leftColor, backgroundColor: leftColor + '10' }
            }}
          >
            Left
          </Button>
          <Button 
            size="small" 
            variant="outlined" 
            onClick={() => toggleDatasetVisibility(1)}
            sx={{ 
              color: rightColor, 
              borderColor: rightColor,
              '&:hover': { borderColor: rightColor, backgroundColor: rightColor + '10' }
            }}
          >
            Right
          </Button>
          <Button size="small" variant="outlined" onClick={zoomIn}>
            Zoom In
          </Button>
          <Button size="small" variant="outlined" onClick={zoomOut}>
            Zoom Out
          </Button>
          <Button size="small" variant="outlined" onClick={resetZoom}>
            Reset
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ height: height }}>
        <Line ref={chartRef} data={chartData} options={options} />
      </Box>

      {/* Instructions */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        💡 <strong>Interactive Features:</strong> Mouse wheel to zoom • Drag to pan • Hover for detailed values • Left/Right buttons toggle visibility
      </Typography>
    </Paper>
  );
}
